import React, { useState, useEffect } from 'react';
import '../../styles/components.css';
import { fetchAuth } from '../../utils/api';

function BillSettingsView({ handleApiError, showNotification, user, fetchCompanySettings }) {
    const [settings, setSettings] = useState({
        companyName: '',
        companyAddress: '',
        companyContact: '',
        companyGstin: '',
        isGstEnabled: false,
        gstPercentage: 18,
        termsAndConditions: 'Goods once sold will not be taken back.\nThis is a computer generated invoice.',
        showTerms: true,
        showHsn: true,
    });
    const [logoPreview, setLogoPreview] = useState(null);

    useEffect(() => {
        fetchAuth('/api/bill-settings')
            .then(res => res.ok ? res.json() : {})
            .then(data => {
                if (data) {
                    setSettings(prev => ({
                        ...prev,
                        ...data,
                        showTerms: data.showTerms !== false,
                        showHsn: data.showHsn !== false,
                        termsAndConditions: data.termsAndConditions || prev.termsAndConditions,
                    }));
                    setLogoPreview(data.companyLogo);
                }
            })
            .catch(handleApiError);
    }, [handleApiError]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
                setSettings(prev => ({ ...prev, companyLogo: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await fetchAuth('/api/bill-settings', {
                method: 'POST',
                body: JSON.stringify(settings)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to save settings');
            // Update local state from the saved response (camelCase from backend)
            setSettings(prev => ({ ...prev, ...data }));
            showNotification('Settings saved successfully!', 'success');
            if (fetchCompanySettings) fetchCompanySettings();
        } catch (err) {
            handleApiError(err);
        }
    };

    return (
        <div className="settings-page">
            <div className="page-header">
                <h2 className="page-title">Company &amp; Bill Settings</h2>
            </div>
            <form onSubmit={handleSubmit} className="settings-form-card">
                <div className="settings-grid">
                    {/* Column 1: Company Details */}
                    <div className="form-column">
                        <h3>Company Details</h3>
                        <div className="input-group">
                            <label>Bill Title (shown as heading on bill)</label>
                            <input
                                name="billTitle"
                                value={settings.billTitle || ''}
                                onChange={handleChange}
                                placeholder="e.g. Bill of Supply, Tax Invoice, Estimate..."
                            />
                        </div>
                        <div className="input-group">
                            <label>Company Name</label>
                            <input name="companyName" value={settings.companyName || ''} onChange={handleChange} />
                        </div>
                        <div className="input-group">
                            <label>Address</label>
                            <textarea name="companyAddress" value={settings.companyAddress || ''} onChange={handleChange} rows="3"></textarea>
                        </div>
                        <div className="input-group">
                            <label>Contact Info (Email/Phone)</label>
                            <input name="companyContact" value={settings.companyContact || ''} onChange={handleChange} />
                        </div>
                        <div className="input-group">
                            <label>GSTIN</label>
                            <input name="companyGstin" value={settings.companyGstin || ''} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Column 2: Logo + GST + Bill Options */}
                    <div className="form-column">
                        <h3>Company Logo</h3>
                        <div className="logo-uploader">
                            {logoPreview ?
                                <img src={logoPreview} alt="Logo Preview" className="logo-preview" /> :
                                <div className="logo-placeholder"><span>Your Logo</span></div>
                            }
                            <input type="file" id="logo-upload" accept="image/*" onChange={handleLogoChange} />
                            <label htmlFor="logo-upload" className="upload-btn">Choose File</label>
                            <p>Upload a JPG or PNG. Recommended size: 150x150 pixels.</p>
                        </div>
                        <hr className="form-divider" />
                        <h3>GST Settings</h3>
                        <div className="gst-options">
                            <label htmlFor="isGstEnabled">Enable GST on Bills?</label>
                            <input
                                type="checkbox"
                                id="isGstEnabled"
                                name="isGstEnabled"
                                checked={settings.isGstEnabled || false}
                                onChange={handleChange}
                            />
                        </div>
                        {settings.isGstEnabled && (
                            <div className="input-group">
                                <label>GST Percentage (%)</label>
                                <input
                                    type="number"
                                    name="gstPercentage"
                                    value={settings.gstPercentage || ''}
                                    onChange={handleChange}
                                />
                            </div>
                        )}
                        <hr className="form-divider" />
                        <h3>Bill Display Options</h3>
                        <div className="gst-options">
                            <label htmlFor="showHsn">Show HSN/SAC Code on Bill?</label>
                            <input
                                type="checkbox"
                                id="showHsn"
                                name="showHsn"
                                checked={settings.showHsn !== false}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="gst-options" style={{ marginTop: '8px' }}>
                            <label htmlFor="showTerms">Show Terms &amp; Conditions on Bill?</label>
                            <input
                                type="checkbox"
                                id="showTerms"
                                name="showTerms"
                                checked={settings.showTerms !== false}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Terms & Conditions textarea — full width below */}
                {settings.showTerms && (
                    <div className="input-group" style={{ marginTop: '16px' }}>
                        <label>Terms &amp; Conditions Text</label>
                        <textarea
                            name="termsAndConditions"
                            value={settings.termsAndConditions || ''}
                            onChange={handleChange}
                            rows="4"
                            placeholder="Enter your bill terms and conditions..."
                            style={{ width: '100%' }}
                        ></textarea>
                    </div>
                )}

                <div className="form-actions">
                    <button type="submit" className="save-button">Save Settings</button>
                </div>
            </form>
        </div>
    );
}

export default BillSettingsView;
